import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { View, ViewOff, Router } from '@carbon/icons-react';
import { useTreeReducer } from '../Tree/useTree';
import { Tree } from '../Tree/Tree';
import * as THREE from 'three';
import { TreeNode } from '../Tree';
import { useStore } from '@slope/store';
import { calculateFitArgs } from '../../utils';

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 100%;
  padding: 1rem;
`;

export type SideBarProps = {
  children?: React.ReactNode;
};

const parseObjectTree = (objects: THREE.Object3D[]): TreeNode[] => {
  return objects.map((node) => ({
    id: node.uuid,
    label: node.name,
    children: node.children.length > 0 ? parseObjectTree(node.children) : [],
  }));
};

export const SideBar: React.FC<SideBarProps> = () => {
  const storeSet = useStore(({ set }) => set);
  const scene = useStore(({ scene }) => scene);
  const controls = useStore(({ controls }) => controls);

  const { state, toggleNode, refreshTree, selectNode } = useTreeReducer();

  const trackObject = useCallback(
    (object: THREE.Object3D, camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) => {
      const { position, target } = calculateFitArgs(object, camera, 1.2);

      controls?.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, true);
    },
    [controls],
  );

  const Visible: React.FC<{ id: string }> = useCallback(
    ({ id }) => {
      const object = scene.getObjectByProperty('uuid', id);
      const handleClick = () => {
        if (object) {
          object.visible = !object.visible;
          refreshTree();
        }
      };
      return object?.visible ? <View onClick={handleClick} /> : <ViewOff onClick={handleClick} />;
    },
    [refreshTree, scene],
  );

  const Track: React.FC<{ id: string }> = useCallback(
    ({ id }) => {
      const object = scene.getObjectByProperty('uuid', id);
      const handleClick = () => {
        if (object && controls) {
          console.log('Tracking object:', object.name);
          trackObject(object, controls.camera);
          if (scene.uuid !== object.uuid) {
            storeSet({
              selection: [object],
            });
            selectNode(id);
          }
        }
      };
      return <Router onClick={handleClick} />;
    },
    [controls, scene, selectNode, storeSet, trackObject],
  );

  const objectTree = useMemo(() => parseObjectTree([scene]), [scene]);

  useEffect(() => {
    const selection = Array.from(state.entries()).reduce((acc, [id, value]) => {
      if (value.selected) {
        const object = scene.getObjectByProperty('uuid', id);
        return object ? [...acc, object] : acc;
      }
      return acc;
    }, [] as THREE.Object3D[]);
    storeSet({
      selection,
    });
  }, [scene, state, storeSet]);

  return (
    <Flex>
      {!!objectTree.length && (
        <Tree
          nodes={objectTree}
          state={state}
          onToggleCollapse={toggleNode}
          onNodeClick={selectNode}
          actions={[Track, Visible]}
        />
      )}
    </Flex>
  );
};
